import React, { type CSSProperties } from 'react';
import Breadcrumbs, { type Crumb } from '../../navigation/breadcrumbs/Breadcrumbs';
import {
    DefaultPartnerLink,
    getPartnerChipKey,
    getPartnerInitials,
    getPartnerLogoAlt,
    getPartnerLogoSource,
    getPartnerMediaSource,
    getPartnerSchemaTypeUrl,
    getReadablePartnerText,
    isPartnerChip,
    makePartnerSoftAccent,
    partnerTokens,
    renderPartnerIcon,
    resolvePartnerLinkProps,
    safePartnerJsonLd,
    toPartnerCssSize,
    type PartnerAction,
    type PartnerChip,
    type PartnerColors,
    type PartnerIcon,
    type PartnerIconRenderer,
    type PartnerIconToken,
    type PartnerImage,
    type PartnerImageSource,
    type PartnerLinkComponent,
    type PartnerLogo,
    type PartnerMetric,
    type PartnerSchemaType,
} from '../partner-shared';

export const partnerProfileShowcaseTokens = {
    colors: partnerTokens.colors,
    sizes: {
        maxWidth: 1180,
    },
} as const;

export type PartnerProfileShowcaseBreadcrumbItem = Crumb;

export type PartnerProfileIdentity = {
    name: React.ReactNode;
    href: string;
    logo?: PartnerLogo;
    logoSrc?: PartnerImageSource;
    logoAlt?: string;
    description?: React.ReactNode;
    category?: React.ReactNode;
    relationship?: React.ReactNode;
    location?: React.ReactNode;
    sameAs?: string | string[];
    schemaType?: PartnerSchemaType;
    target?: string;
    rel?: string | false;
    linkRel?: string | false;
    sponsored?: boolean;
    nofollow?: boolean;
    ugc?: boolean;
};

export type PartnerProfileRecommendation = {
    label?: React.ReactNode;
    summary?: React.ReactNode;
    rating?: number;
    maxRating?: number;
    ariaLabel?: string;
};

export type PartnerProfileDetail = {
    id?: string | number;
    label: React.ReactNode;
    value: React.ReactNode;
    href?: string;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
    icon?: PartnerIcon;
    iconComponent?: PartnerIconRenderer;
    iconToken?: PartnerIconToken;
};

export type PartnerProfileContentSection = {
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    body?: React.ReactNode;
    paragraphs?: React.ReactNode[];
};

export type PartnerProfileAdmiredWorkItem = {
    id?: string | number;
    title: React.ReactNode;
    description?: React.ReactNode;
    href?: string;
    ariaLabel?: string;
    titleAttribute?: string;
    target?: string;
    rel?: string;
    icon?: PartnerIcon;
    iconComponent?: PartnerIconRenderer;
    iconToken?: PartnerIconToken;
};

export type PartnerProfileStrength = {
    id?: string | number;
    title: React.ReactNode;
    description?: React.ReactNode;
    icon?: PartnerIcon;
    iconComponent?: PartnerIconRenderer;
    iconToken?: PartnerIconToken;
};

export type PartnerProfileCta = {
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    primaryAction?: PartnerAction | false;
    secondaryAction?: PartnerAction | false;
};

export type PartnerProfileShowcaseProps = {
    partner: PartnerProfileIdentity;
    LinkComponent?: PartnerLinkComponent;
    Link?: PartnerLinkComponent;
    breadcrumbItems?: PartnerProfileShowcaseBreadcrumbItem[];
    breadcrumbAriaLabel?: string;
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    titleComponent?: 'h1' | 'h2' | 'h3';
    description?: React.ReactNode;
    heroImage?: PartnerImage | string | false;
    heroImageAlt?: string;
    recommendation?: PartnerProfileRecommendation | false;
    details?: PartnerProfileDetail[];
    stats?: PartnerMetric[];
    profile?: PartnerProfileContentSection;
    collaboration?: PartnerProfileContentSection;
    admiredWork?: PartnerProfileContentSection & {
        items?: PartnerProfileAdmiredWorkItem[];
    };
    strengths?: PartnerProfileContentSection & {
        items?: PartnerProfileStrength[];
        tags?: PartnerChip[];
    };
    cta?: PartnerProfileCta | false;
    primaryAction?: PartnerAction | false;
    secondaryAction?: PartnerAction | false;
    disclosure?: React.ReactNode;
    defaultLinkRel?: string | false;
    defaultLinkTarget?: string;
    includeSchema?: boolean;
    schemaId?: string;
    schemaOverride?: Record<string, unknown>;
    colors?: PartnerColors;
    colours?: PartnerColors;
    accentColor?: string;
    accentColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    maxWidth?: number | string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type PartnerProfileShowcaseStyle = CSSProperties &
    Record<`--partner-profile-showcase-${string}`, string | number | undefined>;

const partnerProfileShowcaseCss = `
.partner-profile-showcase {
  --partner-profile-showcase-bg: ${partnerTokens.colors.background};
  --partner-profile-showcase-surface: ${partnerTokens.colors.surface};
  --partner-profile-showcase-surface-soft: ${partnerTokens.colors.surfaceSoft};
  --partner-profile-showcase-text: ${partnerTokens.colors.text};
  --partner-profile-showcase-text-soft: ${partnerTokens.colors.textSoft};
  --partner-profile-showcase-text-muted: ${partnerTokens.colors.textMuted};
  --partner-profile-showcase-accent: ${partnerTokens.colors.accent};
  --partner-profile-showcase-accent-dark: ${partnerTokens.colors.accentDark};
  --partner-profile-showcase-accent-soft: ${partnerTokens.colors.accentSoft};
  --partner-profile-showcase-action: ${partnerTokens.colors.action};
  --partner-profile-showcase-action-soft: ${partnerTokens.colors.actionSoft};
  --partner-profile-showcase-border: ${partnerTokens.colors.border};
  --partner-profile-showcase-border-strong: ${partnerTokens.colors.borderStrong};
  --partner-profile-showcase-chip-bg: ${partnerTokens.colors.chipBackground};
  --partner-profile-showcase-logo-bg: ${partnerTokens.colors.logoBackground};
  --partner-profile-showcase-media-fallback: ${partnerTokens.colors.mediaFallback};
  --partner-profile-showcase-star: ${partnerTokens.colors.star};
  --partner-profile-showcase-shadow: ${partnerTokens.colors.shadow};
  --partner-profile-showcase-max-width: ${partnerProfileShowcaseTokens.sizes.maxWidth}px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  background: var(--partner-profile-showcase-bg);
  color: var(--partner-profile-showcase-text);
  font-family: ${partnerTokens.typography.fontFamily};
  box-sizing: border-box;
}
.partner-profile-showcase,
.partner-profile-showcase * {
  box-sizing: border-box;
}
.partner-profile-showcase-shell {
  width: 100%;
  max-width: var(--partner-profile-showcase-max-width);
  margin: 0 auto;
  padding: 0 24px;
}
.partner-profile-showcase-breadcrumb-wrap {
  padding: 20px 0 8px;
}
.partner-profile-showcase-breadcrumb-wrap nav {
  padding-left: 0;
  padding-right: 0;
}
.partner-profile-showcase-hero {
  padding: clamp(30px, 5vw, 64px) 0 clamp(36px, 5vw, 66px);
}
.partner-profile-showcase-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(310px, 0.62fr);
  gap: clamp(24px, 4vw, 46px);
  align-items: stretch;
}
.partner-profile-showcase-hero-copy {
  display: grid;
  align-content: center;
  min-width: 0;
  gap: 18px;
}
.partner-profile-showcase-eyebrow,
.partner-profile-showcase-section-eyebrow,
.partner-profile-showcase-cta-eyebrow {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--partner-profile-showcase-accent);
  font-size: 12px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  overflow-wrap: anywhere;
}
.partner-profile-showcase-eyebrow::after,
.partner-profile-showcase-section-eyebrow::after,
.partner-profile-showcase-cta-eyebrow::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
}
.partner-profile-showcase-title {
  max-width: 820px;
  margin: 0;
  color: var(--partner-profile-showcase-text);
  font-size: clamp(40px, 5vw, 72px);
  line-height: 1.02;
  font-weight: 870;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-profile-showcase-title-highlight {
  color: var(--partner-profile-showcase-accent);
}
.partner-profile-showcase-description {
  max-width: 760px;
  margin: 0;
  color: var(--partner-profile-showcase-text-soft);
  font-size: 17px;
  line-height: 1.62;
  font-weight: 500;
  letter-spacing: 0;
}
.partner-profile-showcase-recommendation {
  display: grid;
  max-width: 720px;
  gap: 8px;
  border-left: 4px solid var(--partner-profile-showcase-accent);
  background: color-mix(in srgb, var(--partner-profile-showcase-surface) 76%, var(--partner-profile-showcase-accent-soft));
  padding: 14px 16px;
}
.partner-profile-showcase-recommendation-top {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.partner-profile-showcase-recommendation-label {
  color: var(--partner-profile-showcase-text);
  font-size: 14px;
  line-height: 1.3;
  font-weight: 850;
  letter-spacing: 0;
}
.partner-profile-showcase-stars {
  display: inline-flex;
  gap: 3px;
  align-items: center;
  color: color-mix(in srgb, var(--partner-profile-showcase-star) 28%, #d1d5db);
}
.partner-profile-showcase-star--filled {
  color: var(--partner-profile-showcase-star);
}
.partner-profile-showcase-star svg {
  fill: currentColor;
}
.partner-profile-showcase-recommendation-summary {
  margin: 0;
  color: var(--partner-profile-showcase-text-soft);
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
  letter-spacing: 0;
}
.partner-profile-showcase-actions,
.partner-profile-showcase-cta-actions,
.partner-profile-showcase-panel-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.partner-profile-showcase-action {
  display: inline-flex;
  max-width: 100%;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border-radius: 8px;
  padding: 0 17px;
  text-decoration: none;
  font-size: 14px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
}
.partner-profile-showcase-action--primary {
  border: 1px solid var(--partner-profile-showcase-action);
  background: var(--partner-profile-showcase-action);
  color: #ffffff;
}
.partner-profile-showcase-action--secondary,
.partner-profile-showcase-action--text {
  border: 1px solid color-mix(in srgb, var(--partner-profile-showcase-action) 28%, var(--partner-profile-showcase-border));
  background: var(--partner-profile-showcase-surface);
  color: var(--partner-profile-showcase-action);
}
.partner-profile-showcase-action--text {
  min-height: 0;
  border-color: transparent;
  background: transparent;
  padding: 0;
}
.partner-profile-showcase-action:focus-visible,
.partner-profile-showcase-detail-link:focus-visible,
.partner-profile-showcase-work-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--partner-profile-showcase-action) 28%, transparent);
  outline-offset: 3px;
}
.partner-profile-showcase-action-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.partner-profile-showcase-action:hover .partner-profile-showcase-icon {
  transform: translateX(3px);
}
.partner-profile-showcase-panel {
  display: grid;
  min-width: 0;
  align-content: start;
  gap: 18px;
  border: 1px solid var(--partner-profile-showcase-border);
  border-radius: 8px;
  background: var(--partner-profile-showcase-surface);
  padding: 20px;
  box-shadow: var(--partner-profile-showcase-shadow);
}
.partner-profile-showcase-logo-lockup {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 15px;
  align-items: center;
}
.partner-profile-showcase-logo {
  display: flex;
  width: 84px;
  height: 84px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--partner-profile-showcase-accent) 14%, var(--partner-profile-showcase-border));
  border-radius: 8px;
  background: var(--partner-profile-showcase-logo-bg);
  color: var(--partner-profile-showcase-accent);
  font-size: 24px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0;
}
.partner-profile-showcase-logo-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  padding: 12px;
}
.partner-profile-showcase-panel-name {
  margin: 0;
  color: var(--partner-profile-showcase-text);
  font-size: 24px;
  line-height: 1.12;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-profile-showcase-panel-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin: 8px 0 0;
  color: var(--partner-profile-showcase-text-muted);
  font-size: 12px;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: 0;
}
.partner-profile-showcase-panel-meta span + span::before {
  content: "/";
  margin-right: 8px;
  color: color-mix(in srgb, var(--partner-profile-showcase-text-muted) 58%, transparent);
}
.partner-profile-showcase-relationship {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  gap: 7px;
  border-radius: 999px;
  background: var(--partner-profile-showcase-accent-soft);
  color: var(--partner-profile-showcase-accent);
  padding: 8px 11px;
  font-size: 12px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-profile-showcase-panel-description {
  margin: 0;
  color: var(--partner-profile-showcase-text-soft);
  font-size: 14px;
  line-height: 1.55;
  font-weight: 500;
  letter-spacing: 0;
}
.partner-profile-showcase-details {
  display: grid;
  gap: 10px;
  margin: 0;
}
.partner-profile-showcase-detail {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  min-width: 0;
}
.partner-profile-showcase-detail-icon {
  display: inline-flex;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--partner-profile-showcase-accent-soft);
  color: var(--partner-profile-showcase-accent);
}
.partner-profile-showcase-detail-label {
  margin: 0 0 3px;
  color: var(--partner-profile-showcase-text-muted);
  font-size: 11px;
  line-height: 1.2;
  font-weight: 850;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.partner-profile-showcase-detail-value,
.partner-profile-showcase-detail-link {
  color: var(--partner-profile-showcase-text);
  font-size: 14px;
  line-height: 1.4;
  font-weight: 750;
  letter-spacing: 0;
  text-decoration: none;
  overflow-wrap: anywhere;
}
.partner-profile-showcase-detail-link {
  color: var(--partner-profile-showcase-action);
}
.partner-profile-showcase-media {
  overflow: hidden;
  border: 1px solid var(--partner-profile-showcase-border);
  border-radius: 8px;
  background: var(--partner-profile-showcase-media-fallback);
}
.partner-profile-showcase-media img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  object-position: center;
}
.partner-profile-showcase-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
  padding: 0 0 clamp(34px, 4vw, 54px);
}
.partner-profile-showcase-stat {
  display: grid;
  min-width: 0;
  gap: 8px;
  border: 1px solid var(--partner-profile-showcase-border);
  border-radius: 8px;
  background: var(--partner-profile-showcase-surface);
  padding: 16px;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.04);
}
.partner-profile-showcase-stat-value {
  color: var(--partner-profile-showcase-accent);
  font-size: 28px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-profile-showcase-stat-label {
  color: var(--partner-profile-showcase-text);
  font-size: 13px;
  line-height: 1.3;
  font-weight: 820;
  letter-spacing: 0;
}
.partner-profile-showcase-stat-description {
  margin: 0;
  color: var(--partner-profile-showcase-text-soft);
  font-size: 13px;
  line-height: 1.45;
  font-weight: 500;
  letter-spacing: 0;
}
.partner-profile-showcase-section {
  padding: clamp(34px, 5vw, 62px) 0;
  border-top: 1px solid var(--partner-profile-showcase-border);
}
.partner-profile-showcase-section-grid {
  display: grid;
  grid-template-columns: minmax(240px, 0.48fr) minmax(0, 1fr);
  gap: clamp(22px, 4vw, 44px);
  align-items: start;
}
.partner-profile-showcase-section-header {
  display: grid;
  min-width: 0;
  gap: 10px;
}
.partner-profile-showcase-section-title {
  margin: 0;
  color: var(--partner-profile-showcase-text);
  font-size: clamp(26px, 3vw, 38px);
  line-height: 1.1;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-profile-showcase-section-description {
  margin: 0;
  color: var(--partner-profile-showcase-text-soft);
  font-size: 15px;
  line-height: 1.58;
  font-weight: 500;
  letter-spacing: 0;
}
.partner-profile-showcase-rich-text {
  display: grid;
  gap: 14px;
  min-width: 0;
  color: var(--partner-profile-showcase-text-soft);
  font-size: 16px;
  line-height: 1.68;
  font-weight: 500;
  letter-spacing: 0;
}
.partner-profile-showcase-rich-text p {
  margin: 0;
}
.partner-profile-showcase-card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
.partner-profile-showcase-info-card,
.partner-profile-showcase-work-card {
  display: grid;
  min-width: 0;
  align-content: start;
  gap: 10px;
  border: 1px solid var(--partner-profile-showcase-border);
  border-radius: 8px;
  background: var(--partner-profile-showcase-surface);
  padding: 16px;
}
.partner-profile-showcase-info-icon,
.partner-profile-showcase-work-icon {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--partner-profile-showcase-accent-soft);
  color: var(--partner-profile-showcase-accent);
}
.partner-profile-showcase-card-title {
  margin: 0;
  color: var(--partner-profile-showcase-text);
  font-size: 16px;
  line-height: 1.25;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-profile-showcase-card-text {
  margin: 0;
  color: var(--partner-profile-showcase-text-soft);
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
  letter-spacing: 0;
}
.partner-profile-showcase-work-link {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  gap: 7px;
  color: var(--partner-profile-showcase-action);
  text-decoration: none;
  font-size: 13px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
}
.partner-profile-showcase-chip-list {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 8px;
}
.partner-profile-showcase-chip,
.partner-profile-showcase-chip-link {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  border-radius: 999px;
  background: var(--partner-profile-showcase-chip-surface, var(--partner-profile-showcase-chip-bg));
  color: var(--partner-profile-showcase-chip-text, var(--partner-profile-showcase-text-soft));
  padding: 8px 11px;
  text-decoration: none;
  font-size: 12px;
  line-height: 1;
  font-weight: 760;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-profile-showcase-cta {
  padding: clamp(34px, 5vw, 62px) 0;
}
.partner-profile-showcase-cta-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 22px;
  align-items: center;
  border: 1px solid color-mix(in srgb, var(--partner-profile-showcase-accent) 24%, var(--partner-profile-showcase-border));
  border-radius: 8px;
  background: color-mix(in srgb, var(--partner-profile-showcase-surface) 82%, var(--partner-profile-showcase-accent-soft));
  padding: clamp(20px, 3vw, 30px);
}
.partner-profile-showcase-cta-copy {
  display: grid;
  gap: 10px;
  min-width: 0;
}
.partner-profile-showcase-cta-title {
  margin: 0;
  color: var(--partner-profile-showcase-text);
  font-size: clamp(24px, 3vw, 34px);
  line-height: 1.12;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-profile-showcase-cta-description {
  max-width: 720px;
  margin: 0;
  color: var(--partner-profile-showcase-text-soft);
  font-size: 15px;
  line-height: 1.55;
  font-weight: 500;
  letter-spacing: 0;
}
.partner-profile-showcase-disclosure {
  max-width: 820px;
  margin: 0;
  padding: 0 0 26px;
  color: var(--partner-profile-showcase-text-muted);
  font-size: 12px;
  line-height: 1.55;
  font-weight: 550;
  letter-spacing: 0;
}
.partner-profile-showcase-icon,
.partner-profile-showcase-icon svg {
  display: block;
  width: var(--partner-profile-showcase-icon-size, 16px);
  height: var(--partner-profile-showcase-icon-size, 16px);
  flex: 0 0 auto;
}
.partner-profile-showcase-icon {
  transition: transform 180ms ease;
}
@supports not (color: color-mix(in srgb, red 50%, white)) {
  .partner-profile-showcase-action--primary {
    background: var(--partner-profile-showcase-action);
  }
}
@media (min-width: 921px) {
  .partner-profile-showcase-hero-grid {
    align-items: start;
  }
  .partner-profile-showcase-hero-copy {
    align-content: start;
    padding-top: clamp(14px, 1.8vw, 28px);
  }
  .partner-profile-showcase-media img {
    aspect-ratio: 16 / 8.7;
  }
}
@media (max-width: 920px) {
  .partner-profile-showcase-hero-grid,
  .partner-profile-showcase-section-grid,
  .partner-profile-showcase-cta-panel {
    grid-template-columns: 1fr;
  }
  .partner-profile-showcase-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 620px) {
  .partner-profile-showcase-shell {
    padding: 0 18px;
  }
  .partner-profile-showcase-title {
    font-size: 38px;
  }
  .partner-profile-showcase-logo-lockup {
    grid-template-columns: 1fr;
  }
  .partner-profile-showcase-card-grid,
  .partner-profile-showcase-stats {
    grid-template-columns: 1fr;
  }
  .partner-profile-showcase-action {
    width: 100%;
  }
}
`;

function shouldRender(value: React.ReactNode) {
    return value !== undefined && value !== null && value !== false && value !== '';
}

function PartnerProfileLogo({ partner }: { partner: PartnerProfileIdentity }) {
    const logoSrc = getPartnerLogoSource(partner.logo, partner.logoSrc);
    const dimensions =
        partner.logo?.src && typeof partner.logo.src === 'object'
            ? {
                  width: partner.logo.width || partner.logo.src.width,
                  height: partner.logo.height || partner.logo.src.height,
              }
            : { width: partner.logo?.width, height: partner.logo?.height };

    return (
        <span className="partner-profile-showcase-logo" aria-hidden={logoSrc ? undefined : true}>
            {logoSrc ? (
                <img
                    className="partner-profile-showcase-logo-image"
                    src={logoSrc}
                    alt={getPartnerLogoAlt({
                        logo: partner.logo,
                        logoAlt: partner.logoAlt,
                        name: partner.name,
                    })}
                    width={dimensions.width}
                    height={dimensions.height}
                    loading={partner.logo?.loading || 'lazy'}
                    decoding={partner.logo?.decoding || 'async'}
                    itemProp="logo"
                    style={{
                        objectFit: partner.logo?.objectFit,
                        objectPosition: partner.logo?.objectPosition,
                    }}
                />
            ) : (
                <span>{getPartnerInitials(partner.name)}</span>
            )}
        </span>
    );
}

function PartnerRating({ recommendation }: { recommendation: PartnerProfileRecommendation }) {
    const maxRating = Math.max(1, Math.round(recommendation.maxRating || 5));
    const rating =
        typeof recommendation.rating === 'number'
            ? Math.max(0, Math.min(maxRating, recommendation.rating))
            : 0;
    const roundedRating = Math.round(rating);

    if (!rating) return null;

    return (
        <span
            className="partner-profile-showcase-stars"
            aria-label={
                recommendation.ariaLabel ||
                `Recommended rating ${rating} out of ${maxRating}`
            }
        >
            {Array.from({ length: maxRating }).map((_, index) => (
                <span
                    className={[
                        'partner-profile-showcase-star',
                        index < roundedRating ? 'partner-profile-showcase-star--filled' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    key={index}
                >
                    {renderPartnerIcon({
                        iconToken: 'star',
                        size: 16,
                        className: 'partner-profile-showcase-icon',
                        cssVariable: '--partner-profile-showcase-icon-size',
                    })}
                </span>
            ))}
        </span>
    );
}

function renderAction({
    action,
    fallback,
    ComponentLink,
    defaultLinkRel,
    defaultLinkTarget,
}: {
    action?: PartnerAction | false;
    fallback?: PartnerAction | false;
    ComponentLink: PartnerLinkComponent;
    defaultLinkRel?: string | false;
    defaultLinkTarget?: string;
}) {
    const resolvedAction = action === undefined ? fallback : action;
    if (!resolvedAction) return null;

    const linkProps = resolvePartnerLinkProps({
        rel: resolvedAction.rel,
        linkRel: resolvedAction.linkRel,
        target: resolvedAction.target,
        sponsored: resolvedAction.sponsored,
        nofollow: resolvedAction.nofollow,
        ugc: resolvedAction.ugc,
        defaultRel: defaultLinkRel,
        defaultTarget: defaultLinkTarget,
    });
    const variant = resolvedAction.variant || 'primary';

    return (
        <ComponentLink
            href={resolvedAction.href}
            className={[
                'partner-profile-showcase-action',
                `partner-profile-showcase-action--${variant}`,
            ].join(' ')}
            aria-label={resolvedAction.ariaLabel}
            title={resolvedAction.title}
            {...linkProps}
        >
            {renderPartnerIcon({
                icon: resolvedAction.icon,
                iconComponent: resolvedAction.iconComponent,
                iconToken:
                    resolvedAction.iconToken ||
                    (variant === 'primary' ? 'externalLink' : 'arrowRight'),
                size: 17,
                className: 'partner-profile-showcase-icon',
                cssVariable: '--partner-profile-showcase-icon-size',
            })}
            <span className="partner-profile-showcase-action-text">
                {resolvedAction.label}
            </span>
            {variant !== 'text'
                ? renderPartnerIcon({
                      iconToken: 'arrowRight',
                      size: 15,
                      className: 'partner-profile-showcase-icon',
                      cssVariable: '--partner-profile-showcase-icon-size',
                  })
                : null}
        </ComponentLink>
    );
}

function SectionText({ section }: { section?: PartnerProfileContentSection }) {
    if (!section) return null;

    return (
        <div className="partner-profile-showcase-rich-text">
            {section.body ? (
                React.isValidElement(section.body) ? (
                    section.body
                ) : (
                    <p>{section.body}</p>
                )
            ) : null}
            {section.paragraphs?.map((paragraph, index) =>
                React.isValidElement(paragraph) ? (
                    <React.Fragment key={index}>{paragraph}</React.Fragment>
                ) : (
                    <p key={index}>{paragraph}</p>
                ),
            )}
        </div>
    );
}

function buildPartnerSchema({
    partner,
    description,
    schemaId,
    schemaOverride,
}: {
    partner: PartnerProfileIdentity;
    description?: React.ReactNode;
    schemaId?: string;
    schemaOverride?: Record<string, unknown>;
}) {
    const name = getReadablePartnerText(partner.name);
    if (!name) return null;

    const logo = getPartnerLogoSource(partner.logo, partner.logoSrc);
    const resolvedDescription =
        getReadablePartnerText(partner.description) || getReadablePartnerText(description);
    const sameAs = Array.isArray(partner.sameAs)
        ? partner.sameAs
        : partner.sameAs
          ? [partner.sameAs]
          : undefined;
    const schema = {
        '@context': 'https://schema.org',
        '@type': partner.schemaType || 'Organization',
        ...(schemaId ? { '@id': schemaId } : {}),
        name,
        url: partner.href,
        ...(logo ? { logo } : {}),
        ...(resolvedDescription ? { description: resolvedDescription } : {}),
        ...(sameAs?.length ? { sameAs } : {}),
        ...schemaOverride,
    };

    return schema;
}

export default function PartnerProfileShowcase({
    partner,
    LinkComponent,
    Link,
    breadcrumbItems,
    breadcrumbAriaLabel = 'Partner breadcrumb',
    eyebrow = 'Recommended partner',
    title,
    titleComponent = 'h1',
    description,
    heroImage,
    heroImageAlt,
    recommendation,
    details = [],
    stats = [],
    profile,
    collaboration,
    admiredWork,
    strengths,
    cta,
    primaryAction,
    secondaryAction,
    disclosure,
    defaultLinkRel,
    defaultLinkTarget,
    includeSchema = false,
    schemaId,
    schemaOverride,
    colors,
    colours,
    accentColor,
    accentColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    maxWidth,
    className,
    style,
    ariaLabel,
}: PartnerProfileShowcaseProps) {
    const ComponentLink = LinkComponent || Link || DefaultPartnerLink;
    const mergedColors = { ...colors, ...colours };
    const resolvedAccent =
        accentColor || accentColour || mergedColors.accent || partnerTokens.colors.accent;
    const sectionStyle: PartnerProfileShowcaseStyle = {
        ...style,
        '--partner-profile-showcase-bg':
            backgroundColor || backgroundColour || mergedColors.background,
        '--partner-profile-showcase-surface':
            surfaceColor || surfaceColour || mergedColors.surface,
        '--partner-profile-showcase-surface-soft': mergedColors.surfaceSoft,
        '--partner-profile-showcase-text': textColor || textColour || mergedColors.text,
        '--partner-profile-showcase-text-soft': mergedColors.textSoft,
        '--partner-profile-showcase-text-muted': mergedColors.textMuted,
        '--partner-profile-showcase-accent': resolvedAccent,
        '--partner-profile-showcase-accent-dark': mergedColors.accentDark,
        '--partner-profile-showcase-accent-soft':
            mergedColors.accentSoft || makePartnerSoftAccent(resolvedAccent),
        '--partner-profile-showcase-action': mergedColors.action,
        '--partner-profile-showcase-action-soft': mergedColors.actionSoft,
        '--partner-profile-showcase-border': mergedColors.border,
        '--partner-profile-showcase-border-strong': mergedColors.borderStrong,
        '--partner-profile-showcase-chip-bg': mergedColors.chipBackground,
        '--partner-profile-showcase-logo-bg': mergedColors.logoBackground,
        '--partner-profile-showcase-media-fallback': mergedColors.mediaFallback,
        '--partner-profile-showcase-star': mergedColors.star,
        '--partner-profile-showcase-shadow': mergedColors.shadow,
        '--partner-profile-showcase-max-width': toPartnerCssSize(maxWidth),
    };
    const TitleTag = titleComponent;
    const resolvedTitle = title || partner.name;
    const resolvedDescription = description || partner.description;
    const defaultPrimaryAction: PartnerAction = {
        label: `Visit ${getReadablePartnerText(partner.name, 'partner')}`,
        href: partner.href,
        target: partner.target,
        rel: partner.rel,
        linkRel: partner.linkRel,
        sponsored: partner.sponsored,
        nofollow: partner.nofollow,
        ugc: partner.ugc,
        iconToken: 'externalLink',
        variant: 'primary',
    };
    const defaultSecondaryAction: PartnerAction | false = false;
    const heroMediaSrc = heroImage === false ? '' : getPartnerMediaSource(heroImage);
    const schema = includeSchema
        ? buildPartnerSchema({
              partner,
              description: resolvedDescription,
              schemaId,
              schemaOverride,
          })
        : null;
    const hasProfileSection =
        !!profile &&
        !!(profile.title || profile.description || profile.body || profile.paragraphs?.length);
    const hasCollaborationSection =
        !!collaboration &&
        !!(
            collaboration.title ||
            collaboration.description ||
            collaboration.body ||
            collaboration.paragraphs?.length
        );
    const hasAdmiredWork = !!admiredWork?.items?.length;
    const hasStrengths = !!(strengths?.items?.length || strengths?.tags?.length);
    const hasCta = cta !== false && !!cta;
    const panelLinkProps = resolvePartnerLinkProps({
        rel: partner.rel,
        linkRel: partner.linkRel,
        target: partner.target,
        sponsored: partner.sponsored,
        nofollow: partner.nofollow,
        ugc: partner.ugc,
        defaultRel: defaultLinkRel,
        defaultTarget: defaultLinkTarget,
    });

    return (
        <section
            className={['partner-profile-showcase', className].filter(Boolean).join(' ')}
            style={sectionStyle}
            aria-label={ariaLabel}
            data-component="partner-profile-showcase"
        >
            <style>{partnerProfileShowcaseCss}</style>
            {schema ? (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: safePartnerJsonLd(schema) }}
                />
            ) : null}

            <div className="partner-profile-showcase-shell">
                {breadcrumbItems?.length ? (
                    <div className="partner-profile-showcase-breadcrumb-wrap">
                        <Breadcrumbs
                            crumbs={breadcrumbItems}
                            LinkComponent={ComponentLink}
                            ariaLabel={breadcrumbAriaLabel}
                            homeAccent={resolvedAccent}
                            currentPageAccent={resolvedAccent}
                        />
                    </div>
                ) : null}

                <div className="partner-profile-showcase-hero">
                    <div className="partner-profile-showcase-hero-grid">
                        <div className="partner-profile-showcase-hero-copy">
                            {eyebrow ? (
                                <p className="partner-profile-showcase-eyebrow">{eyebrow}</p>
                            ) : null}
                            {resolvedTitle ? (
                                <TitleTag className="partner-profile-showcase-title">
                                    {resolvedTitle}
                                </TitleTag>
                            ) : null}
                            {resolvedDescription ? (
                                <p className="partner-profile-showcase-description">
                                    {resolvedDescription}
                                </p>
                            ) : null}

                            {recommendation !== false && recommendation ? (
                                <div className="partner-profile-showcase-recommendation">
                                    <div className="partner-profile-showcase-recommendation-top">
                                        {recommendation.label ? (
                                            <span className="partner-profile-showcase-recommendation-label">
                                                {recommendation.label}
                                            </span>
                                        ) : null}
                                        <PartnerRating recommendation={recommendation} />
                                    </div>
                                    {recommendation.summary ? (
                                        <p className="partner-profile-showcase-recommendation-summary">
                                            {recommendation.summary}
                                        </p>
                                    ) : null}
                                </div>
                            ) : null}

                            <div className="partner-profile-showcase-actions">
                                {renderAction({
                                    action: primaryAction,
                                    fallback: defaultPrimaryAction,
                                    ComponentLink,
                                    defaultLinkRel,
                                    defaultLinkTarget,
                                })}
                                {renderAction({
                                    action: secondaryAction,
                                    fallback: defaultSecondaryAction,
                                    ComponentLink,
                                    defaultLinkRel,
                                    defaultLinkTarget,
                                })}
                            </div>
                        </div>

                        <aside
                            className="partner-profile-showcase-panel"
                            itemScope
                            itemType={getPartnerSchemaTypeUrl(partner.schemaType)}
                        >
                            <div className="partner-profile-showcase-logo-lockup">
                                <PartnerProfileLogo partner={partner} />
                                <div>
                                    <h2
                                        className="partner-profile-showcase-panel-name"
                                        itemProp="name"
                                    >
                                        {partner.name}
                                    </h2>
                                    {partner.category || partner.location ? (
                                        <p className="partner-profile-showcase-panel-meta">
                                            {shouldRender(partner.category) ? (
                                                <span>{partner.category}</span>
                                            ) : null}
                                            {shouldRender(partner.location) ? (
                                                <span>{partner.location}</span>
                                            ) : null}
                                        </p>
                                    ) : null}
                                </div>
                            </div>

                            {partner.relationship ? (
                                <span className="partner-profile-showcase-relationship">
                                    {renderPartnerIcon({
                                        iconToken: 'handshake',
                                        size: 14,
                                        className: 'partner-profile-showcase-icon',
                                        cssVariable: '--partner-profile-showcase-icon-size',
                                    })}
                                    <span>{partner.relationship}</span>
                                </span>
                            ) : null}

                            {partner.description ? (
                                <p
                                    className="partner-profile-showcase-panel-description"
                                    itemProp="description"
                                >
                                    {partner.description}
                                </p>
                            ) : null}

                            {details.length ? (
                                <dl className="partner-profile-showcase-details">
                                    {details.map((detail, index) => {
                                        const content = detail.href ? (
                                            <ComponentLink
                                                href={detail.href}
                                                className="partner-profile-showcase-detail-link"
                                                aria-label={detail.ariaLabel}
                                                title={detail.title}
                                                target={detail.target}
                                                rel={detail.rel}
                                            >
                                                {detail.value}
                                            </ComponentLink>
                                        ) : (
                                            <span className="partner-profile-showcase-detail-value">
                                                {detail.value}
                                            </span>
                                        );

                                        return (
                                            <div
                                                className="partner-profile-showcase-detail"
                                                key={detail.id || index}
                                            >
                                                <span className="partner-profile-showcase-detail-icon">
                                                    {renderPartnerIcon({
                                                        icon: detail.icon,
                                                        iconComponent: detail.iconComponent,
                                                        iconToken: detail.iconToken || 'badgeCheck',
                                                        size: 15,
                                                        className:
                                                            'partner-profile-showcase-icon',
                                                        cssVariable:
                                                            '--partner-profile-showcase-icon-size',
                                                    })}
                                                </span>
                                                <div>
                                                    <dt className="partner-profile-showcase-detail-label">
                                                        {detail.label}
                                                    </dt>
                                                    <dd style={{ margin: 0 }}>{content}</dd>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </dl>
                            ) : null}

                            <div className="partner-profile-showcase-panel-actions">
                                <ComponentLink
                                    href={partner.href}
                                    className="partner-profile-showcase-action partner-profile-showcase-action--secondary"
                                    itemProp="url"
                                    {...panelLinkProps}
                                >
                                    <span className="partner-profile-showcase-action-text">
                                        Visit website
                                    </span>
                                    {renderPartnerIcon({
                                        iconToken: 'externalLink',
                                        size: 15,
                                        className: 'partner-profile-showcase-icon',
                                        cssVariable: '--partner-profile-showcase-icon-size',
                                    })}
                                </ComponentLink>
                            </div>

                            {heroMediaSrc ? (
                                <figure className="partner-profile-showcase-media">
                                    <img
                                        src={heroMediaSrc}
                                        alt={
                                            heroImageAlt ||
                                            (typeof heroImage === 'object'
                                                ? heroImage.alt || heroImage.title || ''
                                                : '')
                                        }
                                        loading={
                                            typeof heroImage === 'object'
                                                ? heroImage.loading || 'lazy'
                                                : 'lazy'
                                        }
                                        decoding={
                                            typeof heroImage === 'object'
                                                ? heroImage.decoding || 'async'
                                                : 'async'
                                        }
                                        style={{
                                            objectFit:
                                                typeof heroImage === 'object'
                                                    ? heroImage.objectFit
                                                    : undefined,
                                            objectPosition:
                                                typeof heroImage === 'object'
                                                    ? heroImage.objectPosition
                                                    : undefined,
                                        }}
                                    />
                                </figure>
                            ) : null}
                        </aside>
                    </div>
                </div>

                {stats.length ? (
                    <dl className="partner-profile-showcase-stats">
                        {stats.map((stat, index) => (
                            <div
                                className="partner-profile-showcase-stat"
                                aria-label={stat.ariaLabel}
                                key={stat.id || index}
                            >
                                <dt className="partner-profile-showcase-stat-label">
                                    {stat.label}
                                </dt>
                                <dd className="partner-profile-showcase-stat-value">
                                    {stat.value}
                                </dd>
                                {stat.description ? (
                                    <p className="partner-profile-showcase-stat-description">
                                        {stat.description}
                                    </p>
                                ) : null}
                            </div>
                        ))}
                    </dl>
                ) : null}

                {hasProfileSection ? (
                    <section className="partner-profile-showcase-section">
                        <div className="partner-profile-showcase-section-grid">
                            <header className="partner-profile-showcase-section-header">
                                {profile?.eyebrow ? (
                                    <p className="partner-profile-showcase-section-eyebrow">
                                        {profile.eyebrow}
                                    </p>
                                ) : null}
                                {profile?.title ? (
                                    <h2 className="partner-profile-showcase-section-title">
                                        {profile.title}
                                    </h2>
                                ) : null}
                                {profile?.description ? (
                                    <p className="partner-profile-showcase-section-description">
                                        {profile.description}
                                    </p>
                                ) : null}
                            </header>
                            <SectionText section={profile} />
                        </div>
                    </section>
                ) : null}

                {hasCollaborationSection ? (
                    <section className="partner-profile-showcase-section">
                        <div className="partner-profile-showcase-section-grid">
                            <header className="partner-profile-showcase-section-header">
                                {collaboration?.eyebrow ? (
                                    <p className="partner-profile-showcase-section-eyebrow">
                                        {collaboration.eyebrow}
                                    </p>
                                ) : null}
                                {collaboration?.title ? (
                                    <h2 className="partner-profile-showcase-section-title">
                                        {collaboration.title}
                                    </h2>
                                ) : null}
                                {collaboration?.description ? (
                                    <p className="partner-profile-showcase-section-description">
                                        {collaboration.description}
                                    </p>
                                ) : null}
                            </header>
                            <SectionText section={collaboration} />
                        </div>
                    </section>
                ) : null}

                {hasAdmiredWork ? (
                    <section className="partner-profile-showcase-section">
                        <div className="partner-profile-showcase-section-grid">
                            <header className="partner-profile-showcase-section-header">
                                {admiredWork?.eyebrow ? (
                                    <p className="partner-profile-showcase-section-eyebrow">
                                        {admiredWork.eyebrow}
                                    </p>
                                ) : null}
                                {admiredWork?.title ? (
                                    <h2 className="partner-profile-showcase-section-title">
                                        {admiredWork.title}
                                    </h2>
                                ) : null}
                                {admiredWork?.description ? (
                                    <p className="partner-profile-showcase-section-description">
                                        {admiredWork.description}
                                    </p>
                                ) : null}
                            </header>

                            <div className="partner-profile-showcase-card-grid">
                                {admiredWork?.items?.map((item, index) => {
                                    const content = (
                                        <>
                                            <span className="partner-profile-showcase-work-icon">
                                                {renderPartnerIcon({
                                                    icon: item.icon,
                                                    iconComponent: item.iconComponent,
                                                    iconToken: item.iconToken || 'sparkles',
                                                    size: 16,
                                                    className: 'partner-profile-showcase-icon',
                                                    cssVariable:
                                                        '--partner-profile-showcase-icon-size',
                                                })}
                                            </span>
                                            <h3 className="partner-profile-showcase-card-title">
                                                {item.title}
                                            </h3>
                                            {item.description ? (
                                                <p className="partner-profile-showcase-card-text">
                                                    {item.description}
                                                </p>
                                            ) : null}
                                        </>
                                    );

                                    return (
                                        <article
                                            className="partner-profile-showcase-work-card"
                                            key={item.id || index}
                                        >
                                            {content}
                                            {item.href ? (
                                                <ComponentLink
                                                    href={item.href}
                                                    className="partner-profile-showcase-work-link"
                                                    aria-label={item.ariaLabel}
                                                    title={item.titleAttribute}
                                                    target={item.target}
                                                    rel={item.rel}
                                                >
                                                    <span>View example</span>
                                                    {renderPartnerIcon({
                                                        iconToken: 'arrowRight',
                                                        size: 14,
                                                        className:
                                                            'partner-profile-showcase-icon',
                                                        cssVariable:
                                                            '--partner-profile-showcase-icon-size',
                                                    })}
                                                </ComponentLink>
                                            ) : null}
                                        </article>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                ) : null}

                {hasStrengths ? (
                    <section className="partner-profile-showcase-section">
                        <div className="partner-profile-showcase-section-grid">
                            <header className="partner-profile-showcase-section-header">
                                {strengths?.eyebrow ? (
                                    <p className="partner-profile-showcase-section-eyebrow">
                                        {strengths.eyebrow}
                                    </p>
                                ) : null}
                                {strengths?.title ? (
                                    <h2 className="partner-profile-showcase-section-title">
                                        {strengths.title}
                                    </h2>
                                ) : null}
                                {strengths?.description ? (
                                    <p className="partner-profile-showcase-section-description">
                                        {strengths.description}
                                    </p>
                                ) : null}
                            </header>

                            <div>
                                {strengths?.items?.length ? (
                                    <div className="partner-profile-showcase-card-grid">
                                        {strengths.items.map((item, index) => (
                                            <article
                                                className="partner-profile-showcase-info-card"
                                                key={item.id || index}
                                            >
                                                <span className="partner-profile-showcase-info-icon">
                                                    {renderPartnerIcon({
                                                        icon: item.icon,
                                                        iconComponent: item.iconComponent,
                                                        iconToken: item.iconToken || 'badgeCheck',
                                                        size: 16,
                                                        className:
                                                            'partner-profile-showcase-icon',
                                                        cssVariable:
                                                            '--partner-profile-showcase-icon-size',
                                                    })}
                                                </span>
                                                <h3 className="partner-profile-showcase-card-title">
                                                    {item.title}
                                                </h3>
                                                {item.description ? (
                                                    <p className="partner-profile-showcase-card-text">
                                                        {item.description}
                                                    </p>
                                                ) : null}
                                            </article>
                                        ))}
                                    </div>
                                ) : null}

                                {strengths?.tags?.length ? (
                                    <div
                                        className="partner-profile-showcase-chip-list"
                                        style={strengths.items?.length ? { marginTop: 14 } : undefined}
                                    >
                                        {strengths.tags.map((tag, index) => {
                                            const chip = isPartnerChip(tag) ? tag : { label: tag };
                                            const chipStyle = {
                                                '--partner-profile-showcase-chip-surface':
                                                    chip.backgroundColor || chip.backgroundColour,
                                                '--partner-profile-showcase-chip-text':
                                                    chip.textColor ||
                                                    chip.textColour ||
                                                    chip.color ||
                                                    chip.colour,
                                            } as CSSProperties;

                                            if (chip.href) {
                                                return (
                                                    <ComponentLink
                                                        href={chip.href}
                                                        className="partner-profile-showcase-chip-link"
                                                        style={chipStyle}
                                                        target={chip.target}
                                                        rel={chip.rel}
                                                        title={chip.title}
                                                        aria-label={chip.ariaLabel}
                                                        key={getPartnerChipKey(tag, index)}
                                                    >
                                                        {chip.label}
                                                    </ComponentLink>
                                                );
                                            }

                                            return (
                                                <span
                                                    className="partner-profile-showcase-chip"
                                                    style={chipStyle}
                                                    aria-label={chip.ariaLabel}
                                                    key={getPartnerChipKey(tag, index)}
                                                >
                                                    {chip.label}
                                                </span>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </section>
                ) : null}

                {hasCta ? (
                    <section className="partner-profile-showcase-cta">
                        <div className="partner-profile-showcase-cta-panel">
                            <div className="partner-profile-showcase-cta-copy">
                                {cta?.eyebrow ? (
                                    <p className="partner-profile-showcase-cta-eyebrow">
                                        {cta.eyebrow}
                                    </p>
                                ) : null}
                                {cta?.title ? (
                                    <h2 className="partner-profile-showcase-cta-title">
                                        {cta.title}
                                    </h2>
                                ) : null}
                                {cta?.description ? (
                                    <p className="partner-profile-showcase-cta-description">
                                        {cta.description}
                                    </p>
                                ) : null}
                            </div>
                            <div className="partner-profile-showcase-cta-actions">
                                {renderAction({
                                    action: cta?.primaryAction,
                                    ComponentLink,
                                    defaultLinkRel,
                                    defaultLinkTarget,
                                })}
                                {renderAction({
                                    action: cta?.secondaryAction,
                                    ComponentLink,
                                    defaultLinkRel,
                                    defaultLinkTarget,
                                })}
                            </div>
                        </div>
                    </section>
                ) : null}

                {disclosure ? (
                    <p className="partner-profile-showcase-disclosure">{disclosure}</p>
                ) : null}
            </div>
        </section>
    );
}
